import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import {
  Appearance,
  Folder,
  IAdvancedFilters,
  IPageInfo,
  Message,
  MessageStatus,
  Post,
  SpotPolicy,
  Tag,
  User,
  ViewType
} from '../../state/types';
import UserFace from '../../../../common/user-face';
import { Options as MessageActionOptions } from '../../state/actions/approve-items';
import MessageContent from './message-content';
import MessageDetails from './message-details';
import MessageActions from './message-actions';
import { ModalType } from '../../modals/state/types';
import { track } from 'react-tracking';

export interface Props {
  message: Message;
  user: User;
  post: Post;
  position: number;
  pageInfo: IPageInfo;
  isModerationMode: boolean;
  isSelectedItem: boolean;
  isModerationBarActive: boolean;
  spotPolicy: SpotPolicy;
  activeBarRef?: React.RefObject<HTMLDivElement>;
  searchValue?: string;
  isHighlightTagsEnabled: boolean;
  handleSelectItem: (props) => void;
  approveItems: (messages: Message[], options?: MessageActionOptions) => void;
  rejectItems: (messages: Message[], options?: MessageActionOptions) => void;
  setClientAppearance: ({ id: string, appearance: Appearance, folder: Folder }) => void;
  changePageFilters: ({ filters }: { filters: Partial<IAdvancedFilters> }) => void;
  handleOpenViewConvModal: (message: Message) => void;
  openModal: (modalType, modalProps) => void;
  setConversationPolicy: (props) => void;
  isModerationButtonEnabled: boolean;
  setHighlightTag: (props) => void;
}

const ListItem: React.FC<Props> = ({
  message,
  post,
  user,
  position,
  pageInfo,
  isModerationBarActive = false,
  isModerationMode,
  isSelectedItem,
  activeBarRef,
  searchValue,
  handleSelectItem,
  approveItems,
  rejectItems,
  setClientAppearance,
  changePageFilters,
  handleOpenViewConvModal,
  openModal,
  spotPolicy,
  setConversationPolicy,
  isModerationButtonEnabled,
  setHighlightTag,
  isHighlightTagsEnabled
}) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const ip: string = message.get('ip');
  const userId: string = message.get('userId');
  const userDisplayName: string = message.get('userDisplayName');
  const { folder, viewType } = pageInfo;
  const itemAppearance: Appearance = message.get('clientAppearance');
  const isSearchedByContent = !!(viewType === ViewType.TEXT && searchValue);
  const disableCheckbox =
    folder === Folder.ALL || (folder === Folder.PENDING && !isModerationButtonEnabled);
  const itemWrapper = useRef();
  const approveItem = (message: Message, options) => approveItems([message], options);

  const rejectItem = (message: Message, options) => {
    if (message.get('tags').includes(Tag.FEATURED)) {
      openModal(ModalType.CONFIRM, {
        onConfirm: () => rejectItems([message], options),
        title: 'This comment has been featured',
        buttonName: 'Reject Comment',
        description: `This comment has been featured by a staff member. Are you sure you want to reject it?`
      });
    } else {
      rejectItems([message], options);
    }
  };
  const userIncivilityInfo = message.get('tagsData', {}).user_incivility;

  const handleSelect = (checked: boolean) => {
    const id: string = message.get('id');
    handleSelectItem({ id, checked, cursor: position });
  };

  const navigateToConversation = () => {
    handleOpenViewConvModal(message);
  };

  // Right after the animation ends we want to collapse the approved/rejected item.
  const onAnimationEnd = (event: React.AnimationEvent) => {
    if (event.target === itemWrapper.current) {
      setClientAppearance({
        id: message.get('id'),
        appearance: Appearance.COLLAPSED,
        folder: event.animationName === 'approved' ? Folder.APPROVED : Folder.BLOCKED
      });
    }
  };

  const openAddToBlackListModal = () => {
    openModal(ModalType.ADD_TO_BLOCKLIST, {
      user,
      userId,
      ip
    });
  };

  const openAuditLogModal = (modalProps = {}) => {
    openModal(ModalType.AUDIT_LOG, { ...modalProps, message });
  };

  const wrapperClassName = classnames(
    'list-item-wrap',
    folder !== Folder.ALL && {
      'approved collapsed': isModerationBarActive && itemAppearance === Appearance.APPROVED,
      'rejected collapsed': isModerationBarActive && itemAppearance === Appearance.BLOCKED,
      [`${itemAppearance === Appearance.NORMAL ? itemAppearance : Appearance.COLLAPSED}`]:
        !isModerationBarActive ||
        (itemAppearance !== Appearance.APPROVED && itemAppearance !== Appearance.BLOCKED)
    }
  );

  const className = classnames('list-item', {
    checked: message.isChecked(),
    'not-all': !disableCheckbox,
    'selected-item': isSelectedItem
  });

  useEffect(() => {
    const shouldFocusItem: boolean = isSelectedItem;

    if (shouldFocusItem) {
      activeBarRef?.current?.focus();
    }
  }, [isSelectedItem, activeBarRef]);

  const onCollapsed = () => {
    setIsCollapsed(true);
  };

  const onUncollapsed = () => {
    setIsCollapsed(false);
  };

  return (
    <li
      ref={itemWrapper}
      className={wrapperClassName}
      onAnimationEnd={onAnimationEnd}
      onFocus={onUncollapsed}
      onBlur={onCollapsed}
      onMouseEnter={onUncollapsed}
      onMouseLeave={onCollapsed}
    >
      <div ref={activeBarRef} tabIndex={isSelectedItem ? 0 : -1} className={className}>
        <div className={'message-view'}>
          <UserFace
            hasWhitelisted
            multiSelect
            checked={message.isChecked()}
            userId={userId}
            postedAt={message.get('postedAt')}
            onCheck={handleSelect}
            isMessageEdited={message.get('isEdited', false)}
            isAnonymous={message.get('isAnonymous', false)}
            userDisplayName={userDisplayName}
            messageType={message.get('type')}
            user={user}
            userIncivilityInfo={userIncivilityInfo}
          />
          <div
            className={`message ${
              message.get('status') === MessageStatus.DELETED ? 'deleted-message' : ''
            }`}
          >
            <MessageContent
              message={message}
              isSearchedByContent={isSearchedByContent}
              isCollapsed={isCollapsed}
              setIsCollapsed={setIsCollapsed}
            />
            <MessageDetails
              message={message}
              post={post}
              folder={pageInfo.folder}
              openAuditLogModal={openAuditLogModal}
              setHighlightTag={setHighlightTag}
              isHighlightTagsEnabled={isHighlightTagsEnabled}
              changePageFilters={changePageFilters}
              navigateToConversation={navigateToConversation}
            />
          </div>
          <MessageActions
            message={message}
            folder={folder}
            isModerationMode={isModerationMode}
            approveItem={approveItem}
            rejectItem={rejectItem}
            ip={ip}
            user={user}
            conversation={post}
            viewType={viewType}
            navigateToConversation={navigateToConversation}
            openAddToBlackListModal={openAddToBlackListModal}
            changePageFilters={changePageFilters}
            spotPolicy={spotPolicy}
            setConversationPolicy={setConversationPolicy}
            isModerationButtonEnabled={isModerationButtonEnabled}
            openAuditLogModal={openAuditLogModal}
          />
        </div>
      </div>
    </li>
  );
};

export default track(props => {
  const { message, user, post } = props;

  return {
    moderation_tags: message.get('tags'),
    commenter_name: user.get('displayName') ? user.get('displayName') : user.get('username'),
    posted_at: message.get('postedAt').toUTCString(),
    published: !!message.get('published'),
    status: message.get('status'),
    reports_count: message.get('reportsCount'),
    commenter_ip: message.get('ip'),
    commenter_id: user.get('id'),
    commenter_registered: user.get('isRegistered') ? 'yes' : 'no',
    comment_id: message.get('id'),
    article_name: post?.title,
    message_url: post?.postUrl,
    restricted_words: message.getRestrictedWords()
  };
})(ListItem);
